/**
 * ExclusiveContentListener
 *
 * Mirrors Flutter's ExclusiveContentNotifier Hive stream subscription
 * (lib/domain/notifiers/exclusive_content_notifier.dart) on the web, and
 * additionally starts the Firestore live listener that Flutter starts at app
 * boot via `ExclusiveContentService.listenToExclusiveContentChanges`
 * (lib/services/exclusive_content_service.dart:13-62) — that listener is the
 * source of the SecureStorage changes this component mirrors into Redux.
 *
 * Hydrates the exclusiveContent Redux slice on auth and re-hydrates whenever
 * any SecureStorage entry under the EXCLUSIVE_SUBSCRIPTIONS prefix changes,
 * so synchronous selectors (e.g. selectHasActiveSubscription) always reflect
 * the current set of subscriptions.
 *
 * Renders nothing — mount alongside the other app-level listeners in the
 * app tree.
 *
 * Why eventBus.onPrefix (vs. a fixed key list as in PremiumPackagesListener):
 * the set of watched keys is per-creator and unbounded — one entry per creator
 * the viewer subscribes to. PremiumPackages can enumerate three product IDs
 * up front; here we instead subscribe to the prefix and reload all matching
 * entries via GetExclusiveContentSubscriptionsUseCase on any change.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/presentation/store';
import {
  setExclusiveContentSubscriptions,
  clearExclusiveContentSubscriptions,
} from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';
import { GetExclusiveContentSubscriptionsUseCase } from '@/domain/usecases/exclusiveContentSubscription/getExclusiveContentSubscriptionsUseCase';
import { subscribeToExclusiveContentLiveChangesUseCase } from '@/domain/usecases/exclusiveContentSubscription/subscribeToExclusiveContentLiveChangesUseCase';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { eventBus } from '@/infrastructure/eventBus';

export const ExclusiveContentListener = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const firebaseUserId = useSelector((state: RootState) => state.auth.firebaseUserId);
  // Gate storage-reading effects on !isInitializing so we don't read
  // SecureStorage before LoginPage / AppInitializer have set it up.
  // LoginPage flips isAuthenticated true BEFORE awaiting storage init,
  // so reading on the isAuthenticated edge alone races storage init.
  const isInitializing = useSelector((state: RootState) => state.auth.isInitializing);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(clearExclusiveContentSubscriptions());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || isInitializing) return;

    const reloadSubscriptions = async () => {
      const useCase = new GetExclusiveContentSubscriptionsUseCase();
      const subscriptions = await useCase.execute();
      dispatch(setExclusiveContentSubscriptions(subscriptions));
    };

    void reloadSubscriptions();

    const unsubscribe = eventBus.onPrefix(SECURE_STORAGE_KEYS.EXCLUSIVE_SUBSCRIPTIONS, () => {
      void reloadSubscriptions();
    });

    return unsubscribe;
  }, [isAuthenticated, isInitializing, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !firebaseUserId || isInitializing) return;

    const unsubscribe = subscribeToExclusiveContentLiveChangesUseCase.execute(firebaseUserId);

    return unsubscribe;
  }, [isAuthenticated, firebaseUserId, isInitializing]);

  return null;
};
